/**
 * LocationRefreshService.ts — ROAMER LOCATION REFRESH™ sprint.
 *
 * Orchestration for the "Refresh Local Data" button. This is a WORKFLOW
 * service, not a new engine — it only connects existing architecture:
 *
 *   Button → detect location (GPS → IP → cached base → manual picker)
 *          → RoamerLocationEngine.setCurrentLocation()   (only mutation path)
 *          → builtin adapter publishes LocationChanged   (existing bridge)
 *          → Roamer Core™ location workflow              (fusion refresh,
 *            intelligence recalc, RoamerScoreUpdated / JourneyUpdated /
 *            NotificationSent, then WorkflowCompleted)
 *
 * Detection priority (brief): 1-2 browser GPS (high accuracy, 10s timeout,
 * maximumAge 5min; one retry on timeout) → 3 IP-based approximate location →
 * 4 previously verified base → 5 manual picker (only when everything else
 * fails or permission is denied).
 *
 * No new bus events — only existing contracts fire, all through the one
 * existing LocationChanged forwarding point (no duplicates). No polling.
 * Before re-setting the SAME base, the fusion cache is invalidated for that
 * location only (scoped — other cities stay warm).
 */

import {
  roamerLocationEngine,
  type RoamerLocation,
} from '@/services/RoamerLocationEngine';
import { roamerCore } from '@/services/core';
import { roamerDataFusionEngine } from '@/services/fusion';

// ── Types ─────────────────────────────────────────────────────────────────────

export type DetectionMethod = 'gps' | 'ip' | 'cached-base' | 'manual';
export type RefreshPhase = 'idle' | 'detecting' | 'refreshing' | 'done' | 'error' | 'manual-required';
export type PermissionState = 'granted' | 'denied' | 'prompt' | 'unknown';

export interface RefreshOutcome {
  status: 'success' | 'manual-required' | 'error';
  method: DetectionMethod | null;
  location: RoamerLocation | null;
  message: string;
}

export interface RefreshHistoryEntry {
  at: number;
  method: DetectionMethod;
  locationId: string;
  city: string;
  durationMs: number;
  accuracyM: number | null;
}

export interface LocationRefreshState {
  phase: RefreshPhase;
  method: DetectionMethod | null;
  permission: PermissionState;
  gpsAvailable: boolean;
  lastRefreshAt: number | null;
  lastDurationMs: number | null;
  lastAccuracyM: number | null;
  lastError: string | null;
  history: RefreshHistoryEntry[];
}

interface Coordinates {
  latitude: number;
  longitude: number;
  accuracyM: number | null;
}

/** Injectable boundaries so every path is deterministic under test. */
export interface LocationRefreshDeps {
  engine: typeof roamerLocationEngine;
  core: typeof roamerCore;
  fusion: typeof roamerDataFusionEngine;
  /** Wraps navigator.geolocation.getCurrentPosition (promise). */
  getPosition: (opts: PositionOptions) => Promise<Coordinates>;
  /** IP-based approximate location lookup. Throws on failure. */
  ipLookup: () => Promise<Coordinates>;
  now: () => number;
}

// ── Constants ─────────────────────────────────────────────────────────────────

const HISTORY_KEY = 'nomad:location-refresh:v1';
const HISTORY_CAP = 20;
const GPS_OPTIONS: PositionOptions = { enableHighAccuracy: true, timeout: 10_000, maximumAge: 300_000 };
const IP_ENDPOINT = 'https://ipapi.co/json/';
const IP_TIMEOUT_MS = 5_000;
const WORKFLOW_WAIT_MS = 15_000;

/** Sentinel error kinds so the cascade can branch honestly (exported so
 * injected test boundaries can trigger the same classification). */
export class PermissionDeniedError extends Error {}
export class GpsTimeoutError extends Error {}

// ── Default boundary implementations (browser) ────────────────────────────────

function defaultGetPosition(opts: PositionOptions): Promise<Coordinates> {
  return new Promise((resolve, reject) => {
    if (typeof navigator === 'undefined' || !('geolocation' in navigator)) {
      reject(new Error('Geolocation is not available on this device.'));
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (pos) => resolve({
        latitude: pos.coords.latitude,
        longitude: pos.coords.longitude,
        accuracyM: Number.isFinite(pos.coords.accuracy) ? pos.coords.accuracy : null,
      }),
      (err) => {
        if (err.code === err.PERMISSION_DENIED) reject(new PermissionDeniedError('Location permission denied.'));
        else if (err.code === err.TIMEOUT) reject(new GpsTimeoutError('GPS timed out.'));
        else reject(new Error('Position unavailable.'));
      },
      opts,
    );
  });
}

async function defaultIpLookup(): Promise<Coordinates> {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), IP_TIMEOUT_MS);
  try {
    const res = await fetch(IP_ENDPOINT, { signal: controller.signal });
    if (!res.ok) throw new Error(`IP lookup failed (${res.status})`);
    const body = (await res.json()) as { latitude?: unknown; longitude?: unknown };
    const lat = typeof body.latitude === 'number' ? body.latitude : NaN;
    const lng = typeof body.longitude === 'number' ? body.longitude : NaN;
    if (!Number.isFinite(lat) || !Number.isFinite(lng)) throw new Error('IP lookup returned no coordinates.');
    return { latitude: lat, longitude: lng, accuracyM: null };
  } finally {
    clearTimeout(timer);
  }
}

// ── Service ───────────────────────────────────────────────────────────────────

export class LocationRefreshService {
  private deps: LocationRefreshDeps;
  private listeners = new Set<(state: LocationRefreshState) => void>();
  private inFlight: Promise<RefreshOutcome> | null = null;

  private state: LocationRefreshState = {
    phase: 'idle',
    method: null,
    permission: 'unknown',
    gpsAvailable: typeof navigator !== 'undefined' && 'geolocation' in navigator,
    lastRefreshAt: null,
    lastDurationMs: null,
    lastAccuracyM: null,
    lastError: null,
    history: loadHistory(),
  };

  constructor(deps?: Partial<LocationRefreshDeps>) {
    this.deps = {
      engine: roamerLocationEngine,
      core: roamerCore,
      fusion: roamerDataFusionEngine,
      getPosition: defaultGetPosition,
      ipLookup: defaultIpLookup,
      now: () => Date.now(),
      ...deps,
    };
  }

  // ── State / subscription ────────────────────────────────────────────────────

  getState(): LocationRefreshState {
    return { ...this.state, history: [...this.state.history] };
  }

  onChange(listener: (state: LocationRefreshState) => void): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  private setState(patch: Partial<LocationRefreshState>): void {
    this.state = { ...this.state, ...patch };
    const snapshot = this.getState();
    this.listeners.forEach((fn) => {
      try { fn(snapshot); } catch { /* one bad subscriber never breaks the rest */ }
    });
  }

  /** Read the geolocation permission state without prompting. */
  async queryPermission(): Promise<PermissionState> {
    try {
      if (typeof navigator === 'undefined' || !navigator.permissions?.query) return 'unknown';
      const status = await navigator.permissions.query({ name: 'geolocation' as PermissionName });
      const value = (status.state as PermissionState) ?? 'unknown';
      this.setState({ permission: value });
      return value;
    } catch {
      return 'unknown';
    }
  }

  // ── The workflow ────────────────────────────────────────────────────────────

  /** Single-flight: repeated taps while running return the same promise. */
  refreshLocalData(): Promise<RefreshOutcome> {
    if (!this.inFlight) {
      this.inFlight = this.run().finally(() => { this.inFlight = null; });
    }
    return this.inFlight;
  }

  get isRefreshing(): boolean {
    return this.inFlight !== null;
  }

  private async run(): Promise<RefreshOutcome> {
    const startedAt = this.deps.now();
    this.setState({ phase: 'detecting', method: null, lastError: null });
    void this.queryPermission();

    // 1-2. GPS (one retry on timeout, per brief).
    let permissionDenied = false;
    let coords: Coordinates | null = null;
    let method: DetectionMethod | null = null;

    for (let attempt = 0; attempt < 2 && !coords && !permissionDenied; attempt++) {
      try {
        coords = await this.deps.getPosition(GPS_OPTIONS);
        method = 'gps';
        this.setState({ permission: 'granted' });
      } catch (err) {
        if (err instanceof PermissionDeniedError) {
          permissionDenied = true;
          this.setState({ permission: 'denied' });
        } else if (!(err instanceof GpsTimeoutError)) {
          break; // unavailable — retrying won't help; fall through to IP
        }
        // GpsTimeoutError → loop retries exactly once.
      }
    }

    // 3. IP-based approximate location (skipped when permission was denied —
    //    the operator said no to being located; offer manual choice instead).
    if (!coords && !permissionDenied) {
      try {
        coords = await this.deps.ipLookup();
        method = 'ip';
      } catch {
        /* fall through to cached base */
      }
    }

    // Permission denied → offer Use Current Base / Choose Manually. Never block.
    if (permissionDenied) {
      this.setState({ phase: 'manual-required', method: null });
      return {
        status: 'manual-required',
        method: null,
        location: null,
        message: 'Automatic local intelligence needs location access. Use your current base or choose a location manually.',
      };
    }

    let location: RoamerLocation;
    if (coords) {
      // Reverse geocode = offline snap (engine keeps coordinates if the snap
      // fails — an honest coordinate pin, warning logged, never a fabricated city).
      // reverseGeocodeCoordinates: offline snap first, Nominatim reverse
      // geocoding fallback so real cities (e.g. Tucson, AZ) resolve to a name
      // instead of a bare coordinate pin. Degrades gracefully when offline.
      location = await this.deps.engine.reverseGeocodeCoordinates(
        coords.latitude,
        coords.longitude,
        method === 'gps' ? 'gps' : 'search',
      );
      if (location.kind === 'coordinates') {
        // eslint-disable-next-line no-console
        console.warn('[LocationRefresh] reverse geocode failed — keeping honest coordinate pin.');
      }
    } else {
      // 4. Previously verified base (network failure / GPS unavailable).
      location = this.deps.engine.getCurrentLocation();
      method = 'cached-base';
    }

    // Scoped cache invalidation FIRST so re-selecting the same base forces a
    // real provider round-trip. Only this location's entries are dropped.
    try {
      this.deps.fusion.invalidateLocation(location.locationId);
    } catch { /* fusion unavailable — workflow still proceeds */ }

    this.setState({ phase: 'refreshing', method, lastAccuracyM: coords?.accuracyM ?? null });

    // Await the existing core workflow via its own completion event — the ONE
    // LocationChanged forwarding point stays the builtin adapter (no duplicates).
    const workflowDone = this.waitForWorkflowCompleted(location.locationId, startedAt);
    this.deps.engine.setCurrentLocation(location, location.source);
    await workflowDone; // resolves on WorkflowCompleted or after a bounded wait

    const durationMs = this.deps.now() - startedAt;
    const entry: RefreshHistoryEntry = {
      at: this.deps.now(),
      method: method as DetectionMethod,
      locationId: location.locationId,
      city: location.city,
      durationMs,
      accuracyM: coords?.accuracyM ?? null,
    };
    const history = [entry, ...this.state.history].slice(0, HISTORY_CAP);
    persistHistory(history);

    this.setState({
      phase: 'done',
      lastRefreshAt: this.deps.now(),
      lastDurationMs: durationMs,
      history,
    });

    return {
      status: 'success',
      method,
      location,
      message: method === 'cached-base'
        ? `Could not detect a new position — refreshed local intelligence for ${location.city}.`
        : 'Location updated successfully.',
    };
  }

  /**
   * Resolve when the core publishes WorkflowCompleted for this location, or
   * after a bounded wait (core not initialized / workflow denied) — the button
   * must never appear broken.
   */
  private waitForWorkflowCompleted(locationId: string, startedAt: number): Promise<void> {
    return new Promise((resolve) => {
      let settled = false;
      let off: () => void = () => {};
      const finish = () => {
        if (settled) return;
        settled = true;
        off();
        clearTimeout(timer);
        resolve();
      };
      const timer = setTimeout(finish, WORKFLOW_WAIT_MS);
      try {
        off = this.deps.core.on('WorkflowCompleted', (event) => {
          const payload = event.payload as { workflow?: string; locationId?: string };
          // Per-run correlation guard: a delayed completion from a PREVIOUS
          // run (same locationId, published before this run started) must not
          // satisfy this wait — only events at/after our own start count.
          if (
            payload?.workflow === 'locationChanged' &&
            payload?.locationId === locationId &&
            event.at >= startedAt
          ) finish();
        }, 'location-refresh');
      } catch {
        finish(); // core unavailable — resolve immediately, engine update already done
      }
    });
  }

  /** Record a manual-picker resolution so diagnostics reflect priority 5. */
  noteManualSelection(location: RoamerLocation): void {
    const entry: RefreshHistoryEntry = {
      at: this.deps.now(),
      method: 'manual',
      locationId: location.locationId,
      city: location.city,
      durationMs: 0,
      accuracyM: null,
    };
    const history = [entry, ...this.state.history].slice(0, HISTORY_CAP);
    persistHistory(history);
    this.setState({ phase: 'done', method: 'manual', lastRefreshAt: this.deps.now(), history });
  }
}

// ── History persistence (small, capped, sanitized on load) ────────────────────

function loadHistory(): RefreshHistoryEntry[] {
  if (typeof localStorage === 'undefined') return [];
  try {
    const raw = localStorage.getItem(HISTORY_KEY);
    const parsed = raw ? (JSON.parse(raw) as unknown) : null;
    if (!Array.isArray(parsed)) return [];
    return parsed
      .filter((e): e is RefreshHistoryEntry =>
        e != null && typeof e === 'object' &&
        typeof (e as RefreshHistoryEntry).at === 'number' &&
        typeof (e as RefreshHistoryEntry).locationId === 'string' &&
        typeof (e as RefreshHistoryEntry).city === 'string')
      .slice(0, HISTORY_CAP);
  } catch {
    return [];
  }
}

function persistHistory(history: RefreshHistoryEntry[]): void {
  if (typeof localStorage === 'undefined') return;
  try {
    localStorage.setItem(HISTORY_KEY, JSON.stringify(history.slice(0, HISTORY_CAP)));
  } catch { /* storage full — history is diagnostics only */ }
}

/** App-wide singleton. */
export const locationRefreshService = new LocationRefreshService();
